import { ArrowRightIcon, DeleteIcon, StarIcon } from "@chakra-ui/icons";
import {
  Avatar,
  HStack,
  Icon,
  IconButton,
  Link,
  Text,
  VStack,
  chakra,
} from "@chakra-ui/react";
import { KYDPerformerProfile } from "@common/interfaces/KYDEvent";
import { KYDSocialPlatforms } from "@common/interfaces/KYDOrganization";
import {
  DragControls,
  Reorder,
  useDragControls,
  useMotionValue,
} from "framer-motion";
import React, { ReactNode, useCallback } from "react";
import { FaGripVertical, FaRegStar, FaStar } from "react-icons/fa";
import { getIconForSocial } from "../../FeedManager/SocialInput";

const Item = chakra(Reorder.Item);

type Platform = KYDPerformerProfile;

export const PerformerDraggable = ({
  name,
  image,
  readOnly = false,
  id,
  onUpdate,
  platforms = [],
  actions,
}: {
  readOnly: boolean;
  name: string;
  image: string;
  id: string;
  onUpdate: () => void;
  platforms?: Platform[];
  actions: ReactNode;
}) => {
  const dragControls = useDragControls();
  const y = useMotionValue(0);

  const handleMouseDownReorder = useCallback(
    (e: React.PointerEvent, item: any, dc: DragControls) => {
      console.log("mouse down");
      dc.start(e);
      document.addEventListener(
        "mouseup",
        () => {
          console.log("mouse up");
          if (!readOnly) {
            onUpdate();
          }
        },
        { once: true }
      );
    },
    [onUpdate]
  );
  return (
    <Item
      width={"full"}
      id={id}
      value={id}
      key={id}
      dragListener={false}
      style={{ y }}
      dragControls={dragControls}
    >
      <PerformerRow
        name={name}
        id={id}
        image={image}
        platforms={platforms}
        actions={actions}
        dragComponent={
          readOnly ? null : (
            <HStack
              display={{ base: "none", md: "inherit" }}
              cursor={"grab"}
              onPointerDown={(e) => {
                if (!readOnly) {
                  handleMouseDownReorder(e, {}, dragControls);
                }
              }}
              h="50px"
              w={["40px", null, "25px"]}
              justify={"center"}
            >
              <Icon color="gray.400" as={FaGripVertical} />
            </HStack>
          )
        }
      />
    </Item>
  );
};

export const FeatureAction = ({
  name,
  onClick,
  isActive = false,
  isDisabled = false,
}) => (
  <IconButton
    icon={isActive ? <FaStar /> : <FaRegStar />}
    onClick={onClick}
    isDisabled={isDisabled}
    disabled={isDisabled}
    aria-label={`Make ${name} main artist`}
  />
);

export const DeleteAction = ({ name, onClick, isDisabled = false }) => (
  <IconButton
    icon={<DeleteIcon />}
    onClick={onClick}
    isDisabled={isDisabled}
    disabled={isDisabled}
    aria-label={`Delete ${name} from lineup`}
  />
);

export const ConfirmAction = ({ name, onClick }) => (
  <IconButton
    icon={<ArrowRightIcon />}
    onClick={onClick}
    aria-label={`Add ${name} from lineup`}
  />
);

const InlineText = chakra.span;

export const PerformerRow = ({
  name,
  image,
  id,
  platforms = [],
  dragComponent = <React.Fragment></React.Fragment>,
  actions = [],
}: {
  name: string;
  image: string;
  id: string;
  platforms?: Platform[];
  dragComponent?: React.ReactNode;
  actions: React.ReactNode;
}) => {
  return (
    <HStack
      w="full"
      justifyContent={"space-between"}
      borderRadius={"md"}
      userSelect={"none"}
    >
      <HStack
        gap={2}
        maxW={["70%", "80%"]}
        overflow="hidden"
        textOverflow={"ellipsis"}
        wordBreak={"break-all"}
      >
        {dragComponent}
        <Avatar size="md" src={image} />
        <VStack w="full" gap={0} alignItems={"flex-start"}>
          <Text w="full" fontWeight={"bold"} userSelect={"none"}>
            {name}
          </Text>

          <HStack gap={1}>
            {platforms.map((p, i) => {
              if (!p) return null;

              const { icon, placeholder } = getIconForSocial(
                p.platform as KYDSocialPlatforms
              );
              return (
                <Link href={p.url} target="_blank" key={`${p.id}-${i}`}>
                  <HStack gap={1}>
                    {icon}
                    <InlineText w="full" fontSize="xs">
                      {p.display_followers}
                    </InlineText>
                  </HStack>
                </Link>
              );
            })}
          </HStack>
        </VStack>
      </HStack>
      <HStack maxW={["30%", "20%"]}>{actions}</HStack>
    </HStack>
  );
};
